<script lang="ts">
    import type { TrackListFooterLockup } from './types';
    import ChevronIcon from '@amp/web-app-components/assets/icons/chevron.svg';

    export let item: TrackListFooterLockup;
    export let isAlbum: boolean = false;
    export let isPlaylist: boolean = false;
    export let isRecording: boolean = false;
    export let clickComponent;

    $: description = item.description;
    $: linkSection = item.linkSection;
    $: footerVisible = !item.hidden;
</script>

{#if footerVisible}
    <div
        class="tracklist-footer"
        class:tracklist-footer--album={isAlbum}
        class:tracklist-footer--playlist={isPlaylist}
        class:tracklist-footer--recording={isRecording}
        data-testid="tracklist-footer"
    >
        <div class="footer-body">
            {#if description}
                <p
                    class="description"
                    data-testid="tracklist-footer-description"
                >
                    {description}
                </p>
            {/if}

            {#if linkSection && clickComponent}
                <div class="link-section">
                    <h2
                        class="link-section__heading"
                        data-testid="tracklist-footer-link-heading"
                    >
                        {linkSection.title}
                    </h2>
                    <svelte:component
                        this={clickComponent}
                        action={linkSection.link.segue}
                    >
                        <span
                            class="link-section__link"
                            data-testid="tracklist-footer-link"
                        >
                            {linkSection.link.title}<ChevronIcon
                                class="link-chevron"
                                aria-hidden="true"
                            />
                        </span>
                    </svelte:component>
                </div>
            {/if}

            {#if $$slots['cta-button']}
                <div class="tracklist-footer__native-cta-wrapper">
                    <slot name="cta-button" />
                </div>
            {/if}
        </div>

        {#if $$slots['social-badges']}
            <div class="tracklist-footer__friends">
                <slot name="social-badges" />
            </div>
        {/if}
    </div>
{/if}

<style lang="scss">
    @use '@amp/web-shared-styles/sasskit-stylekit/ac-sasskit-config';
    @use 'ac-sasskit/core/locale' as *;
    @use '@amp/web-shared-styles/app/core/globalvars' as *;

    .tracklist-footer {
        margin: 0 var(--bodyGutter);
        padding-bottom: 43px;
        padding-inline-start: $tracklist-footer-start-padding;

        @media (--small) {
            padding-bottom: 53px;
            display: flex;
            flex-direction: row;
            justify-content: space-between;
        }

        // <Section> adds extra space above this container that we don't want.
        // Using this to bump the container upward to the top of the padding.
        :global(.section) & {
            $section-padding-top: 12px;
            margin-top: -$section-padding-top;
        }
    }

    .footer-body {
        position: relative;
        max-width: 440px;
        color: var(--systemSecondary);
        font: var(--body);

        @media (--medium) {
            max-width: 540px;
        }

        @media (--large) {
            max-width: 640px;
        }

        .tracklist-footer--playlist & {
            margin-top: 45px;
        }

        .tracklist-footer--album &,
        .tracklist-footer--recording & {
            margin-top: 44px;
            font: var(--body-tall);

            @media (--small) {
                margin-top: 34px;
            }
        }
    }

    .description {
        white-space: pre-wrap; /* need this to support new lines (\n) and tabs (\t) */
    }

    .link-section {
        margin-top: 21px;

        .link-section__heading {
            margin-bottom: 4px;
            font: var(--subhead-emphasized);
        }

        .link-section__link {
            --text-color: var(--keyColor);
            color: var(--text-color);

            &:hover {
                text-decoration: underline;
            }

            :global(.link-chevron) {
                width: 5px;
                height: 9px;
                fill: var(--text-color);
                vertical-align: baseline;
                margin-inline-start: 5px;
                margin-inline-end: 11px;

                @include rtl {
                    transform: scaleX(-1);
                }
            }
        }
    }

    .tracklist-footer__native-cta-wrapper {
        --buttonHeight: auto;
        --buttonWidth: auto;
        margin-top: 10px;
    }

    .tracklist-footer__friends {
        --social-badge-size: 38px;
        --social-badge-overlap: -8px;
        --social-badge-border-size: 2px;
        --social-badge-border-color: var(--pageBG);

        // Prevent this section from affecting layout when no social
        // badges are rendered.
        &:empty {
            display: none;
        }

        @media (--small) {
            --social-badge-size: 44px;
            --social-badge-overlap: -10px;
            margin-top: 34px;
        }

        @media (--range-xsmall-down) {
            .tracklist-footer--album & {
                margin-top: 27px;
            }

            .tracklist-footer--playlist & {
                margin-top: 25px;
            }
        }
    }
</style>
